import * as React from 'react';
import type {Root} from 'react-dom/client';
import {createRoot} from 'react-dom/client';

import {promisifyModal} from '../modal/utils';
import {ImperativeConfirm} from './component';
import type {ConfirmProps, ConfirmResolution} from './component';
import {ConfirmAttrs, ConfirmImperativeProps, ConfirmParams, THEME} from './types';

/** Matches the delay `promisifyModal` waits before detaching the element it created. */
const HOST_TEARDOWN_DELAY_MS = 500;

/** The event `promisifyModal` resolves for an imperatively-opened dialog. */
export type ConfirmDialogEvent = CustomEvent<ConfirmResolution>;

/**
 * `ConfirmAttrs.style` is a CSS text string, which is what the Panel host took as an attribute.
 * React wants a property object, so it is parsed here rather than widening the FC's prop type.
 */
function parseStyleText(styleText: string): React.CSSProperties {
  return Object.fromEntries(
    styleText
      .split(`;`)
      .map((declaration) => declaration.split(`:`))
      .filter(([property, value]) => property?.trim() && value?.trim())
      .map(([property, value]) => [
        property.trim().replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase()),
        value.trim(),
      ]),
  );
}

function attrsToProps({style, ...attrs}: ConfirmAttrs): Partial<ConfirmProps> {
  const props: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attrs)) {
    props[key.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())] = value;
  }
  if (style) {
    props.style = parseStyleText(style);
  }
  return props as Partial<ConfirmProps>;
}

/**
 * Renders the dialog into the element `promisifyModal` creates rather than handing it a
 * `panelElement` factory. This is the established pattern for React modals here (see
 * `export-dashboard-modal-promise`, `feedback-modal/prompt`) and the only one available:
 * `promisifyModal` resolves a Panel factory's tag name via `_tagName`, which `react2panel` does not
 * set, so passing the bridged `ConfirmEl` would silently create a bare `div` and render nothing.
 */
export function openConfirmDialog(
  attrs: ConfirmAttrs,
  imperativeProps: ConfirmImperativeProps = {},
  params: ConfirmParams = {},
): Promise<ConfirmDialogEvent> {
  let reactRoot: Root | undefined;

  const dialog = promisifyModal<ConfirmDialogEvent>({
    attrs: {},
    closeEvents: [`submit`],
    onElementCreatedCallback: (element) => {
      reactRoot = createRoot(element);
      const onResolve = (detail: ConfirmResolution) => element.dispatchEvent(new CustomEvent(`submit`, {detail}));

      reactRoot.render(
        <ImperativeConfirm {...attrsToProps(attrs)} {...imperativeProps} params={params} onResolve={onResolve} />,
      );
    },
  });

  // Each call mounts its own root, so it has to be torn down or the tree and its i18n subscription
  // outlive the dialog. `promisifyModal` detaches the host on a delay so the close can animate;
  // unmounting any earlier would cut the animation short.
  return dialog.finally(() => {
    setTimeout(() => reactRoot?.unmount(), HOST_TEARDOWN_DELAY_MS);
  });
}

export async function confirm(
  attrs: ConfirmAttrs,
  props: ConfirmImperativeProps = {},
  params: ConfirmParams = {},
): Promise<boolean> {
  const closeEv = await openConfirmDialog({role: `dialog`, ...attrs}, props, params);
  return closeEv.detail.action === `confirm`;
}

export function confirmDelete(attrs: ConfirmAttrs): Promise<boolean> {
  return confirm({...attrs, theme: THEME.DELETE});
}

export async function prompt(
  attrs: ConfirmAttrs,
  props: ConfirmImperativeProps = {},
  params: ConfirmParams = {},
): Promise<{didConfirm: true; inputValue: string} | {didConfirm: false; inputValue: null}> {
  const closeEv = await openConfirmDialog(
    {
      closeable: true,
      'confirm-button-label': `Save`,
      ...attrs,
      elref: `confirmPrompt`,
    },
    props,
    params,
  );

  // Discriminated so callers guarding on `didConfirm` get a non-null `inputValue` without a cast.
  return closeEv.type === `submit` && closeEv.detail.action === `confirm`
    ? {didConfirm: true, inputValue: closeEv.detail.inputValue}
    : {didConfirm: false, inputValue: null};
}
